// modules/website/components/FishSpeciesComponent.tsx - Catálogo de Espécies de Peixes

import React, { useState } from 'react';
import { SpeciesData } from '@/types';
import { Fish, ChevronLeft, ChevronRight, X } from 'lucide-react';

interface Props {
  data: SpeciesData;
  settings?: any;
}

export const FishSpeciesComponent: React.FC<Props> = ({ data, settings }) => {
  const {
    title = 'Espécies',
    species = [],
    layout = 'grid'
  } = data || {};

  const [selectedSpecies, setSelectedSpecies] = useState<typeof species[0] | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Navegação do carrossel
  const nextSlide = () => {
    setCarouselIndex(prev => (prev + 1) % species.length);
  };

  const prevSlide = () => {
    setCarouselIndex(prev => (prev - 1 + species.length) % species.length);
  };

  // Renderizar Grid
  const renderGrid = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {species.map((fish, index) => (
        <div
          key={fish.id || index}
          onClick={() => setSelectedSpecies(fish)}
          className="group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
        >
          {/* Imagem */}
          <div className="aspect-[4/3] overflow-hidden relative">
            {fish.image ? (
              <img
                src={fish.image}
                alt={fish.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                <Fish size={64} className="text-white/50" />
              </div>
            )}
            {/* Overlay com nome científico */}
            {fish.scientificName && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                <p className="text-white/80 text-xs italic">{fish.scientificName}</p>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="p-4">
            <h3 className="text-xl font-bold text-gray-900 mb-2">{fish.name}</h3>
            <p className="text-gray-600 text-sm line-clamp-2 mb-3">{fish.description}</p>
            
            {/* Tags */}
            <div className="flex flex-wrap gap-2">
              {fish.season && (
                <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
                  📅 {fish.season}
                </span>
              )}
              {fish.avgWeight && (
                <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
                  ⚖️ {fish.avgWeight}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Renderizar Carrossel
  const renderCarousel = () => (
    <div className="relative">
      {/* Slides */}
      <div className="overflow-hidden rounded-xl">
        <div 
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${carouselIndex * 100}%)` }}
        >
          {species.map((fish, index) => (
            <div key={fish.id || index} className="w-full flex-shrink-0">
              <div className="flex flex-col md:flex-row bg-white rounded-xl overflow-hidden shadow-xl">
                {/* Imagem */}
                <div className="md:w-1/2 aspect-[4/3] md:aspect-auto">
                  {fish.image ? (
                    <img
                      src={fish.image}
                      alt={fish.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full min-h-[300px] bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                      <Fish size={96} className="text-white/50" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="md:w-1/2 p-8 flex flex-col justify-center">
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">{fish.name}</h3>
                  {fish.scientificName && (
                    <p className="text-gray-500 italic mb-4">{fish.scientificName}</p>
                  )}
                  <p className="text-gray-600 mb-6">{fish.description}</p>
                  
                  {/* Detalhes */}
                  <div className="grid grid-cols-2 gap-4">
                    {fish.season && (
                      <div className="bg-green-50 p-3 rounded-lg">
                        <p className="text-xs text-green-600 font-medium">Temporada</p>
                        <p className="text-green-800 font-bold">{fish.season}</p>
                      </div>
                    )}
                    {fish.avgWeight && (
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-xs text-blue-600 font-medium">Peso Médio</p>
                        <p className="text-blue-800 font-bold">{fish.avgWeight}</p>
                      </div>
                    )}
                    {fish.recordWeight && (
                      <div className="bg-yellow-50 p-3 rounded-lg">
                        <p className="text-xs text-yellow-600 font-medium">Recorde</p>
                        <p className="text-yellow-800 font-bold">🏆 {fish.recordWeight}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navegação */}
      {species.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            aria-label="Espécie anterior"
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-3 shadow-lg transition-all"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={nextSlide}
            aria-label="Próxima espécie"
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-3 shadow-lg transition-all"
          >
            <ChevronRight size={24} />
          </button>

          {/* Indicadores */}
          <div className="flex justify-center gap-2 mt-6">
            {species.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCarouselIndex(idx)}
                aria-label={`Ir para espécie ${idx + 1}`}
                className={`w-3 h-3 rounded-full transition-all ${
                  idx === carouselIndex ? 'bg-blue-600 w-6' : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div 
      className="py-16 px-6"
      style={{ backgroundColor: settings?.backgroundColor || '#f9fafb' }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        {title && (
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-3 mb-4">
              <Fish className="text-blue-600" size={36} />
              <h2 className="text-4xl font-bold text-gray-900">{title}</h2>
            </div>
          </div>
        )}

        {/* Conteúdo */}
        {layout === 'grid' ? renderGrid() : renderCarousel()}

        {/* Empty State */}
        {species.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            <Fish size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg">Nenhuma espécie cadastrada</p>
          </div>
        )}

        {/* Modal de Detalhes */}
        {selectedSpecies && (
          <div 
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedSpecies(null)}
          >
            <div 
              className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <button 
                className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg"
                onClick={() => setSelectedSpecies(null)}
                aria-label="Fechar detalhes"
              >
                <X size={24} />
              </button>

              {selectedSpecies.image && (
                <img
                  src={selectedSpecies.image}
                  alt={selectedSpecies.name}
                  className="w-full h-64 object-cover"
                />
              )}

              <div className="p-6">
                <h3 className="text-3xl font-bold text-gray-900 mb-2">{selectedSpecies.name}</h3>
                {selectedSpecies.scientificName && (
                  <p className="text-gray-500 italic mb-4">{selectedSpecies.scientificName}</p>
                )}
                <p className="text-gray-600 mb-6">{selectedSpecies.description}</p>

                <div className="grid grid-cols-3 gap-4">
                  {selectedSpecies.season && (
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <p className="text-xs text-green-600 font-medium mb-1">Temporada</p>
                      <p className="text-green-800 font-bold">{selectedSpecies.season}</p>
                    </div>
                  )}
                  {selectedSpecies.avgWeight && (
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <p className="text-xs text-blue-600 font-medium mb-1">Peso Médio</p>
                      <p className="text-blue-800 font-bold">{selectedSpecies.avgWeight}</p>
                    </div>
                  )}
                  {selectedSpecies.recordWeight && (
                    <div className="bg-yellow-50 p-4 rounded-lg text-center">
                      <p className="text-xs text-yellow-600 font-medium mb-1">Recorde</p>
                      <p className="text-yellow-800 font-bold">🏆 {selectedSpecies.recordWeight}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
